/**
 * Reports · RCM Register — every purchase bill where the tenant has confirmed
 * that RCM applies. Groups by month and lets the user export to XLSX or open
 * the self-invoice PDF for the URD entries.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, RefreshCw, ShieldCheck } from "lucide-react";

export default function RcmRegister() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/reports/rcm-register",
        { params: { from_date: from || undefined, to_date: to || undefined } });
      setRows(r.data.rows || []);
      setTotals(r.data.totals || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load RCM register");
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const dl = async () => {
    const fname = `RCM-Register-${from || "all"}-to-${to || "all"}.xlsx`;
    await downloadBlob("/reports/rcm-register.xlsx", fname,
      { from_date: from || undefined, to_date: to || undefined });
  };

  const openSelfInvoice = async (bid, bno) => {
    await downloadBlob(`/purchase/bills/${bid}/rcm-self-invoice.pdf`,
      `RCM-SelfInvoice-${bno}.pdf`);
  };

  const inr = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="rcm-register-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// GST · REVERSE CHARGE</div>
          <h1 className="text-3xl font-bold mt-1 font-heading flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-emerald-500" /> RCM Register
          </h1>
          <div className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Every purchase bill where you paid GST under Reverse Charge per Notif 13/2017 CT(R),
            Sec 9(4) URD purchases, or Sec 5(3) import of service. Reconcile these lines against
            your GSTR-3B Table 3.1(d) before filing.
          </div>
        </div>
        <div className="flex gap-2">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="w-40 rounded-sm h-9" data-testid="rcm-from" />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="w-40 rounded-sm h-9" data-testid="rcm-to" />
          <Button onClick={load} disabled={loading} variant="outline"
            className="rounded-sm gap-1.5" data-testid="rcm-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button onClick={dl} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
            data-testid="rcm-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </Button>
        </div>
      </div>

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="rcm-totals">
          {[
            ["Bills", rows.length, "text-white/90"],
            ["Taxable value", inr(totals.taxable), "text-emerald-500"],
            ["CGST", inr(totals.cgst), "text-white/90"],
            ["SGST", inr(totals.sgst), "text-white/90"],
            ["Total GST payable", inr(totals.gst_total), "text-emerald-500"],
          ].map(([lbl, val, tone]) => (
            <div key={lbl} className="card-sharp p-3">
              <div className="text-[10px] font-mono uppercase text-muted-foreground tracking-widest">{lbl}</div>
              <div className={`text-xl font-bold mt-1 font-mono tabular-nums ${tone}`}>{val}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card-sharp overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="p-2 text-left">Bill No</th>
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-left">Vendor</th>
              <th className="p-2 text-left">Notification / Category</th>
              <th className="p-2 text-right">Rate %</th>
              <th className="p-2 text-right">Taxable ₹</th>
              <th className="p-2 text-right">CGST ₹</th>
              <th className="p-2 text-right">SGST ₹</th>
              <th className="p-2 text-right">IGST ₹</th>
              <th className="p-2 text-left">Self-Inv</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rows.map((r) => (
              <tr key={r.bill_id} className="hover:bg-white/5">
                <td className="p-2 font-mono">{r.bill_number}</td>
                <td className="p-2 text-muted-foreground">{r.date}</td>
                <td className="p-2">{r.vendor_name}
                  <div className="text-[10px] font-mono text-muted-foreground">{r.vendor_gstin || "URD"}</div>
                </td>
                <td className="p-2 text-xs">{r.notification}
                  <div className="text-[10px] font-mono text-muted-foreground uppercase">{r.category}</div>
                </td>
                <td className="p-2 text-right font-mono tabular-nums">{r.rate}</td>
                <td className="p-2 text-right font-mono tabular-nums">{inr(r.taxable_value)}</td>
                <td className="p-2 text-right font-mono tabular-nums">{inr(r.cgst)}</td>
                <td className="p-2 text-right font-mono tabular-nums">{inr(r.sgst)}</td>
                <td className="p-2 text-right font-mono tabular-nums">{inr(r.igst)}</td>
                <td className="p-2">
                  {r.self_invoice_required ? (
                    <Button size="sm" variant="ghost" className="h-7 px-2 gap-1"
                      onClick={() => openSelfInvoice(r.bill_id, r.bill_number)}
                      data-testid={`self-inv-${r.bill_id}`}>
                      <FileText className="w-3.5 h-3.5" /> PDF
                    </Button>
                  ) : (
                    <span className="text-[10px] font-mono text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={10} className="p-8 text-center text-muted-foreground text-sm">
                No RCM bills yet. When you save a purchase from a URD/GTA/advocate/etc,
                the system will flag it and ask you to confirm — those bills land here.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
